import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import MaterialRatesTable from 'af-root/scenes/Company/Shared/Materials/MaterialRatesTable';

import type { W_MaterialCompanyMarkupHistory_FindForCompany_VM_Single } from 'ab-api/web/materialCompanyMarkupHistory/findForCompany';
import type { W_MaterialJobMarkupHistory_FindForJob_VM_Single } from 'ab-api/web/materialJobMarkupHistory/findForJob';

import MaterialCostSource from 'ab-enums/materialCostSource.enum';

import * as MaterialJobMarkupHistoryActions from 'af-actions/materialJobMarkupHistory';

import MaterialJobMarkupAdjustmentModal from './MaterialJobMarkupAdjustmentModal';
import MaterialJobMarkupFM from './MaterialJobMarkupAdjustmentModal/formModel';

interface OwnProps {
	jobId: number;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const MaterialJobRatesTable: React.FC<Props> = (props) => {
	const { findForJob, jobId } = props;

	const [markupHistories, setMarkupHistories] = React.useState<W_MaterialJobMarkupHistory_FindForJob_VM_Single[]>([]);
	const [initialFormValue, setInitialFormValue] = React.useState<Nullable<MaterialJobMarkupFM>>(null);

	const loadHistories = React.useCallback(async () => {
		const loaded = await findForJob(jobId);
		if (!loaded) {
			return;
		}
		setMarkupHistories(loaded.histories);
	}, [findForJob, jobId]);

	React.useEffect(() => {
		loadHistories();
	}, [loadHistories]);

	const handleSubmitSuccess = React.useCallback(() => {
		loadHistories();
	}, [loadHistories]);

	const closeModal = React.useCallback(() => {
		setInitialFormValue(null);
	}, []);

	const handleEdit = React.useCallback((initialValueSource: W_MaterialCompanyMarkupHistory_FindForCompany_VM_Single) => {
		setInitialFormValue(new MaterialJobMarkupFM(initialValueSource));
	}, []);

	return (
		<>
			<MaterialRatesTable
				entries={markupHistories}
				onEdit={handleEdit}
				rateSource={MaterialCostSource.JOB}
				title="Rates on Job Level"
				titleTooltip="A historical record of percentage-based rates configured at the job level, including shipping, sales markup, and tax rates. Materials using a job-level rate are marked with a 'J' indicator in the Materials, Job Tracking, and Field Report Work Summary tables — or 'JM' if overridden on the job page."
			/>
			<MaterialJobMarkupAdjustmentModal
				close={closeModal}
				initialValues={initialFormValue}
				jobId={jobId}
				onSubmitSuccess={handleSubmitSuccess}
				showModal={!!initialFormValue}
			/>
		</>
	);
};

function mapDispatchToProps() {
	return {
		findForJob: MaterialJobMarkupHistoryActions.findForJob,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(MaterialJobRatesTable);
